import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Check, ArrowRight, LayoutGrid, DollarSign, BarChart2, Sparkles, Settings, Plug } from 'lucide-react';

const GROUP_ICONS = {
  'Core Operations':         LayoutGrid,
  'Smart Payroll':           DollarSign,
  'Labor Cost Analytics':    BarChart2,
  'AI Workforce Assistant':  Plug,
  'AI Automation (Eva)':     Sparkles,
  'System':                  Settings,
};

const featureGroups = [
  {
    title: 'Core Operations',
    items: [
      'Dashboard', 'Employee Management', 'Roles & Permissions',
      'Time Clock', 'Timesheets', 'Smart Scheduling',
      'Shift Swaps', 'Shift Replacement Workflow', 'Leave Management',
      'Coverage Health', 'Approval Pipelines',
      'Task Management', 'Announcements', 'Notifications',
      'Documents & Acknowledgements',
      'Reports & CSV Export',
      'Staff invitations and email onboarding',
      'Automatic staff notifications',
      'Fast team setup and shift assignment',
    ],
  },
  {
    title: 'Smart Payroll',
    highlight: true,
    items: [
      'Payroll Generation from Timesheets',
      'Payroll Automation',
      'Payroll Summary (PDF + CSV)',
      'Estimated Pay Preview',
      'PDF Payslips + Email Delivery',
      'Resend Failed Payslip Emails',
      'Cash Advance Deduction Tracking',
      'Overtime Tracking & Calculation (1.25x+)',
      'Holiday Pay Rules (2.0x / 1.3x)',
      'Holiday Tracking',
      'Compliance Tracking',
      'Break Compliance Tracking',
    ],
  },
  {
    title: 'Labor Cost Analytics',
    highlight: true,
    items: [
      'Labor Cost Dashboard',
      'Labor vs Productivity Dashboard',
      'Revenue Per Labor Hour (RPLH)',
      'Labor Budget Target',
      'Workforce Analytics',
      'Executive Multi-location Dashboard',
      'Employee Cost Insights',
      'Overtime Detection',
      'Overstaffed Day Detection',
      'Idle Time Detection',
      'Workload Balancing',
      'Location Performance Comparison',
      'CSV Export',
    ],
  },
  {
    title: 'AI Workforce Assistant',
    highlight: true,
    badge: 'Included',
    description: 'Connect ChatGPT or Claude securely using MCP.',
    items: [
      'ChatGPT MCP Integration',
      'Claude MCP Integration',
      'Natural-language Workforce Queries',
      'OAuth 2.1 + PKCE Secured',
      'Tenant-isolated Read-only Access',
      'Granular Scope Permissions (5 read-only scopes)',
      'Audit Log + CSV Export',
      'One-click Disconnect / Revoke',
    ],
  },
  {
    title: 'AI Automation (Eva)',
    highlight: true,
    items: [
      'Eva AI Assistant',
      'AI Operational Insights',
      'AI Workforce Insights',
      'EVA AI Recommendations',
      'AI Announcement Commands',
      'Smart Digests',
      'Shift Risk Detection',
      'Shift Type Intelligence',
      'Automation Recipes',
      'Create Shifts via Chat',
      'Create Tasks via Chat',
      'Post Announcements via Chat',
      'Cancel Shifts via Chat',
    ],
  },
  {
    title: 'System',
    items: [
      'Branded Email Notifications (SMTP)',
      'Billing & Subscription Control',
      'Mobile App Experience',
      'Mobile Clock-In',
      'GPS-ready Clock Logic',
      'Multi-location Management',
      'Unlimited Employees',
      'Unlimited Scheduling',
      'Unlimited Locations (Multi-Location plan)',
      '30-day Free Trial Included',
      'App Installation',
      'Feature Requests',
      'Support Access',
      'Settings & Permissions',
    ],
  },
];

export function PricingSection({ plans, billingCycle, setBillingCycle }) {
  return (
    <section className="py-10 md:py-20 bg-slate-50" id="pricing" aria-label="Pricing">
      <div className="max-w-5xl mx-auto px-4">

        <div className="text-center mb-8 md:mb-12">

          {/* Feature Groups — structured feature system */}
          <div className="mb-14">
            <div className="text-center mb-8">
              <span className="text-xs font-bold uppercase tracking-widest text-orange-500">What's Inside</span>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope' }}>
                Every Plan Includes Everything
              </h3>
              <p className="text-slate-500 text-sm mt-2">No feature gating. No upsells. No limits.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-left">
              {featureGroups.map((group) => {
                const Icon = GROUP_ICONS[group.title];
                return (
                  <div
                    key={group.title}
                    className={`rounded-2xl p-5 border ${
                      group.highlight
                        ? 'border-orange-200 bg-orange-50'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      {Icon && (
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          group.highlight ? 'bg-orange-100' : 'bg-slate-100'
                        }`}>
                          <Icon size={14} className={group.highlight ? 'text-orange-600' : 'text-slate-500'} />
                        </div>
                      )}
                      <h4 className={`text-xs font-bold uppercase tracking-wide ${
                        group.highlight ? 'text-orange-600' : 'text-slate-500'
                      }`}>
                        {group.title}
                      </h4>
                      {group.badge && (
                        <span
                          className="ml-auto text-[9px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5"
                          title={group.description || ''}
                          data-testid="pricing-group-badge"
                        >
                          {group.badge}
                        </span>
                      )}
                    </div>
                    {group.description && (
                      <p className="text-[11px] text-slate-600 mb-3 leading-snug">
                        {group.description}
                      </p>
                    )}
                    <ul className="space-y-2">
                      {group.items.map((item) => (
                        <li key={item} className="flex items-center gap-2 text-sm text-slate-700">
                          <Check
                            size={12}
                            strokeWidth={3}
                            className={`flex-shrink-0 ${group.highlight ? 'text-orange-500' : 'text-slate-400'}`}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Simple, Transparent Pricing
          </h2>
          <p className="mt-4 text-lg text-slate-600">No hidden fees. Cancel anytime.</p>

          {/* Billing Toggle */}
          <div className="mt-8 inline-flex items-center bg-white rounded-xl p-1 border border-slate-200 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${billingCycle === 'monthly' ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:text-slate-900'}`}
              data-testid="billing-monthly-btn"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:text-slate-900'}`}
              data-testid="billing-yearly-btn"
            >
              Yearly
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${billingCycle === 'yearly' ? 'bg-orange-400 text-white' : 'bg-green-100 text-green-700'}`}>
                Save 50%+
              </span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.key}
              className={`relative rounded-2xl p-8 border-2 flex flex-col ${plan.badge ? 'border-orange-400 bg-white shadow-2xl shadow-orange-100' : 'border-slate-200 bg-white shadow-md'}`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full tracking-wide uppercase shadow">
                    {billingCycle === 'yearly' ? 'Best Value' : 'Most Popular'}
                  </span>
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{plan.desc}</p>
                <div className="mt-5">
                  <div className="flex items-end justify-center gap-1">
                    <span className="text-5xl font-extrabold text-slate-900">
                      ${billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyMonthly}
                    </span>
                    <span className="text-slate-500 mb-2 text-base">/mo</span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm font-semibold text-orange-600 mt-1">Billed ${plan.yearlyTotal}/year</p>
                  )}
                  {billingCycle === 'monthly' && (
                    <p className="text-xs text-slate-400 mt-1">Billed monthly, cancel anytime</p>
                  )}
                </div>
              </div>
              <Link to="/register" className="block mb-4">
                <Button
                  className={`w-full text-base font-semibold py-3 ${plan.badge ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
                  data-testid={`pricing-${plan.key}-btn`}
                >
                  {plan.cta}
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
              
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg">
                  <Plug size={14} className="text-orange-600 flex-shrink-0" />
                  <span className="text-xs font-semibold text-orange-700">Includes AI Workforce Assistant</span>
                </div>
              </div>
              
              <p className="text-center text-sm text-slate-700 font-medium mb-6">✔ 30-day free trial • Cancel anytime</p>

              {/* Compact differentiator badges — scannable summary above the full feature list */}
              <div
                className="flex flex-wrap justify-center gap-1.5 mb-6"
                data-testid={`pricing-badges-${plan.key}`}
              >
                {[
                  'AI Workforce Insights',
                  'Payroll Automation',
                  'Compliance Tracking',
                  'Multi-location Ready',
                ].map((label) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200"
                  >
                    <Check size={11} strokeWidth={3} className="flex-shrink-0" />
                    {label}
                  </span>
                ))}
              </div>

              <div className="border-t border-slate-100 pt-6 flex-1">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Everything included</p>
                <ul className="space-y-2.5">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-2.5">
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${plan.badge ? 'bg-orange-100' : 'bg-slate-100'}`}>
                        <Check size={10} className={plan.badge ? 'text-orange-600' : 'text-slate-600'} />
                      </span>
                      <span className="text-sm text-slate-600">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* No spreadsheets tagline */}
        <p className="text-center text-base font-semibold text-slate-700 mt-6 mb-2">
          No spreadsheets. No manual payroll.
        </p>
        <p className="text-center text-sm text-slate-500 max-w-xl mx-auto">
          Get your team started quickly with built-in invites, scheduling, and notifications.
        </p>

        {/* Stripe badge */}
        <div className="mt-8 flex items-center justify-center">
          <div className="inline-flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-6 py-3 shadow-sm">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm3.47 5.97c-.17-.06-.34-.09-.51-.09-.8 0-1.29.62-1.29 1.55v.07h1.6v1.18H9.67V11.5H8.3V8.68H7.5V7.5h.8v-.1c0-1.54.93-2.56 2.4-2.56.3 0 .57.04.77.11v1.02z" fill="#635BFF"/>
            </svg>
            <span className="text-sm text-slate-500 font-medium">Payments by</span>
            <span className="font-bold text-lg tracking-tight" style={{ color: '#635BFF' }}>stripe</span>
          </div>
        </div>

        {/* Comparison image */}
        <div className="mt-8 md:mt-14">
          <img
            src="https://restaff.b-cdn.net/logos/Comparaison.png"
            alt="ReStaff vs competitors — workforce management comparison"
            loading="lazy"
            className="w-full max-w-4xl mx-auto block rounded-2xl shadow-lg"
          />
        </div>

      </div>
    </section>
  );
}
